import type {
  UnresolvedVendorErrorCodeResponseDto,
  UpdateUnresolvedVendorErrorCodeRequestDto,
} from '@evimo/client/models';
import type { UnresolvedVendorErrorCodeFormValues } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/types';
import { UnresolvedVendorErrorCodeApiError } from '~/app/services/errorManagement/unresolved-vendor-error-code-api-error';

function asOptionalString(
  value: string | null | undefined,
): string | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  return value;
}

export function buildUpdateUnresolvedPayload(
  values: UnresolvedVendorErrorCodeFormValues,
  expectedVersion: number,
): UpdateUnresolvedVendorErrorCodeRequestDto {
  // Form values keep enum fields as strings; OpenAPI DTO uses generated enums.
  return {
    manufacturerId: values.manufacturerId,
    assetTypeId: values.assetTypeId,
    errorCode: values.errorCode,
    matchType: asOptionalString(values.matchType) ?? null,
    matchKey: asOptionalString(values.matchKey) ?? null,
    vendorErrorCode: asOptionalString(values.vendorErrorCode) ?? null,
    info: asOptionalString(values.info) ?? null,
    parameter: values.parameter ? values.parameter : null,
    state: values.state ? values.state : null,
    impactedComponent: values.impactedComponent
      ? values.impactedComponent
      : null,
    category: values.category ? values.category : null,
    severity: values.severity ? values.severity : null,
    firmwareVersion: asOptionalString(values.firmwareVersion) ?? null,
    description: asOptionalString(values.description) ?? null,
    sourceDescription: asOptionalString(values.sourceDescription) ?? null,
    remark: values.remark.trim(),
    expectedVersion,
  } as UpdateUnresolvedVendorErrorCodeRequestDto;
}

export function isConflictError(error: unknown): boolean {
  return (
    error instanceof UnresolvedVendorErrorCodeApiError && error.status === 409
  );
}

export function statusTagColor(status: string): string {
  switch (status) {
    case 'OPEN':
      return 'blue';
    case 'IN_REVIEW':
      return 'gold';
    case 'RESOLVED':
      return 'green';
    case 'IGNORED':
      return 'default';
    default:
      return 'default';
  }
}

export function toUnresolvedFormValues(
  record: UnresolvedVendorErrorCodeResponseDto,
): UnresolvedVendorErrorCodeFormValues {
  return {
    manufacturerId: record.manufacturerId,
    assetTypeId: record.assetTypeId,
    errorCode: record.errorCode,
    matchType: record.matchType ?? undefined,
    matchKey: record.matchKey ?? undefined,
    vendorErrorCode: record.vendorErrorCode ?? undefined,
    info: record.info ?? undefined,
    parameter: record.parameter ?? undefined,
    state: record.state ?? undefined,
    impactedComponent: record.impactedComponent ?? undefined,
    category: record.category ?? undefined,
    severity: record.severity ?? undefined,
    firmwareVersion: record.firmwareVersion ?? undefined,
    description: record.description ?? undefined,
    sourceDescription: record.sourceDescription ?? undefined,
    remark: '',
  };
}

export function isUnresolvedEditable(status: string): boolean {
  return status === 'OPEN' || status === 'IN_REVIEW';
}

export function isUnresolvedIgnored(status: string): boolean {
  return status === 'IGNORED';
}
